import React, { forwardRef, useCallback } from 'react';
import { Button, ButtonProps } from "../Button/Button"
import { Modal } from "../Modal/Modal"
import { Text } from "../Text/Text"
import { Stack } from "../Stack/Stack"
import { getIconComponent, Icon, IconType } from "../Icon";
import { Align } from "@mrocket/styles";
import { CloseIcon, DialogBody, DialogHeader, DialogHeaderTitle, DialogRoot, DialogActions } from "./Dialog.styles";

interface DialogAction extends Pick<ButtonProps, 'progress' | 'mode'>{
  label?: string,
  autoClose?: boolean,
  action: () => void,
}

export interface DialogProps extends React.HTMLAttributes<HTMLDivElement> {
  open: boolean
  onClose: () => void
  disablePortal?: boolean
  title?: string
  subtitle?: string
  icon?: IconType
  align?: Align
  // width?: number
  actions?: DialogAction[]
}

/**
 *  Dialog is a container to display content in an overlay window
 * */

export const Dialog = forwardRef<Element, DialogProps>((
  {
    open,
    onClose,
    disablePortal,
    children,
    title,
    subtitle,
    icon,
    actions,
    ...props
  },
  ref
) => {

  const buttonAction = useCallback((callback: () => void, autoClose?: boolean) => {
    if (autoClose) {
      callback()
      onClose()
    } else {
      callback()
    }
  }, [onClose])

  return (
    <Modal
      ref={ref}
      open={open}
      onClose={onClose}
      disablePortal={disablePortal}
    >
      <DialogRoot {...props}>
        <DialogHeader>
          <Stack align={'center'} gap={8}>
            {icon && getIconComponent(icon)}
            <DialogHeaderTitle>{title}</DialogHeaderTitle>
            <Text color={'3'} size={3}>{subtitle}</Text>
          </Stack>
          <CloseIcon onClick={onClose}>
            <Icon name={'xmark'} size={16}/>
          </CloseIcon>
        </DialogHeader>
        <DialogBody>
          {children}
        </DialogBody>
        {actions && (
          <DialogActions>
            {actions.map(({action, label, autoClose = true, mode, progress}, index) => (
              <Button
                key={index}
                size={'small'}
                mode={mode}
                progress={progress}
                appearance={mode !== 'secondary' ? 'fill' : 'outline'}
                onClick={() => buttonAction(action, autoClose)}
              >
                {label}
              </Button>
            ))}
          </DialogActions>
        )}
      </DialogRoot>
    </Modal>
  );
});

Dialog.displayName = 'Dialog'

